import _                                          from 'underscore';
import React, { FC, MutableRefObject, ReactNode } from 'react';
import { Message, List }                          from 'semantic-ui-react';
import { useQuery, QueryHookOptions }             from '@apollo/react-hooks';
import { ApolloError, ApolloQueryResult }         from 'apollo-client';
import { GQLNode }                                from '../../../types';

import { queriesHaveData, queriesHaveErrors, queriesLoading } from '../../../store/_apolloUtil';
import { LoadingIndicator }                                   from '../util/loading/LoadingIndicator';
import { EmptyCollectionMessage }                             from '../util/messaging/EmptyCollectionMessage';
import { ErrorMessageFromGraphQL }                            from '../util/errorHandling/ErrorMessageFromGraphQL';
import { QueryResult }                                        from '@apollo/react-common';
import { EMPTY_VALUE_DISPLAY }                                from '../../../constants/FormatStringEnums';

export interface ListFromQueryItemProps<P = any> {
	key: string,
	item?: P,
	index?: number,
	isLastItem?: boolean,
}

export interface ListFromQueryProps {
	query?: GQLNode,
	queryOptions?: QueryHookOptions,
	containerComponent?: ReactNode,
	itemComponent?: ReactNode,
	itemContentProp?: string,
	innerRef?: null | MutableRefObject<QueryResult>
	
	queryResultObject?( data: ApolloQueryResult<any>['data'] ): any,
	
	loadingComponent?(): any,
	
	noItemsComponent?(): any,
	
	errorComponent?( errors: ApolloError, query?: GQLNode ): any,
	
	[ additionalProps: string ]: any
}

const defaultProps: ListFromQueryProps = {
	query:              null,
	queryOptions:       {},
	containerComponent: null,
	itemComponent:      null,
	itemContentProp:    'id',
	innerRef:           null,
	queryResultObject:  ( data ) => data,
	loadingComponent:   () => <LoadingIndicator isActive={ true } dimmer={ false }/>,
	noItemsComponent:   () => <EmptyCollectionMessage/>,
	errorComponent:     ( response, query ) => <ErrorMessageFromGraphQL response={ response } query={ query }/>,
}

const ListFromQuery: FC<ListFromQueryProps> = ( props ) => {
	
	let collectionReq      = useQuery( props.query, props.queryOptions ),
	    ContainerComponent = ( props.containerComponent as FC<unknown> ) || List,
	    ItemComponent      = ( props.itemComponent as FC<ListFromQueryItemProps> ) || List.Item,
	    items              = [];
	
	if( props.innerRef ){
		props.innerRef.current = collectionReq
	}
	
	if( queriesLoading( collectionReq ) ){
		return _.isFunction( props.loadingComponent )
		       ? props.loadingComponent()
		       : props.loadingComponent;
	}
	
	if( queriesHaveErrors( collectionReq ) ){
		return _.isFunction( props.errorComponent )
		       ? props.errorComponent( collectionReq.error, props.query )
		       : props.errorComponent;
	}
	
	if( queriesHaveData( collectionReq ) ){
		items = props.queryResultObject( collectionReq.data );
	}
	
	if( !items || items.length === 0 ){
		return _.isFunction( props.noItemsComponent )
		       ? props.noItemsComponent()
		       : props.noItemsComponent;
	}
	
	return (
		<ContainerComponent>
			{ items && items.map( ( item: any, index: number ) => {
				return props.itemComponent
				       ? <ItemComponent key={ `item-${ props.itemContentProp }-${ index }` } item={ item } index={ index } isLastItem={ ( index === items.length - 1 ) }/>
				       : <ItemComponent key={ `item-${ props.itemContentProp }-${ index }` }>{ item[ props.itemContentProp ] || EMPTY_VALUE_DISPLAY }</ItemComponent>
			} ) }
		</ContainerComponent>
	);
};

ListFromQuery.defaultProps = defaultProps;

export { ListFromQuery }
